import {
  FaConfig,
  FaIconLibrary,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// FontAwesome Icons
import {
  IconDefinition,
  IconName,
  IconPrefix,
  faTriangleExclamation,
  fas,
} from '@fortawesome/free-solid-svg-icons';
// Third-party modules
import { NgpPopover, NgpPopoverTrigger } from 'ng-primitives/popover';
import { NgpTabset, NgpTabList, NgpTabButton } from 'ng-primitives/tabs';

import { AdvancedQuickSearchComponent } from './components/advanced-quick-search/advanced-quick-search.component';
import { AgGridModule } from 'ag-grid-angular';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AutoStoryToolDialogComponent } from './components/auto-story-tool-dialog/auto-story-tool-dialog.component';
import { LinkRelatedArticlesDialogComponent } from './components/link-related-articles-dialog/link-related-articles-dialog.component';
import { I18nKeyRejectedDialogComponent } from './components/i18n-key-rejected-dialog/i18n-key-rejected-dialog.component';
import { AlgorithmScheduleComponent } from '../core/components/algorithm-schedule/algorithm-schedule.component';
import { BannerContainerComponent } from './components/banner-container/banner-container.component';
// Local Components
import { JsonEditorComponent } from './components/json-editor/json-editor.component';
import { ChatMessageInputComponent } from './components/chat-message-input/chat-message-input.component';
import { ColorPaletteComponent } from './components/color-palette/color-palette.component';
import { ColorPickerComponent } from '@ui/color-picker/color-picker.component';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CompanyBadgeComponent } from './components/company-badge/company-badge.component';
import { CompanySwitcherComponent } from './components/company-switcher/company-switcher.component';
import { ContextualNotFoundComponent } from './components/contextual-not-found/contextual-not-found.component';
// Pipes
import { CountFilteredPipe } from '@pipes/count-filtered.pipe';
import { CountGroupMatchesPipe } from '@pipes/count-group-matches.pipe';
import { CounterBadgeComponent } from './components/counter-badge/counter-badge.component';
import { CustomFilterOptionsPipe } from '@pipes/custom-filter-options.pipe';
import { DashboardAutoRefreshCounterComponent } from './components/dashboard-auto-refresh-counter/dashboard-auto-refresh-counter.component';
import { DataObjectSelectComponent } from '../shared/components/data-object-select/data-object-select.component';
import { DataobjectListSimpleComponent } from './components/dataobject-list-simple/dataobject-list-simple.component';
import { DatasourceSelectorComponent } from './components/datasource-selector/datasource-selector.component';
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component';
import { DateRangePickerDashboardPageComponent } from './components/date-range-picker-dashboard-page/date-range-picker-dashboard-page.component';
import { DataGridModule } from '@sensika/ag-grid-kit';
import { PeriodHistoryComponent } from './components/period-history/period-history.component';
import { FormDateRangePickerComponent } from './components/form-date-range-picker/form-date-range-picker.component';
import { DialogTabNavComponent } from './components/dialog-tab-nav/dialog-tab-nav.component';
import { DropdownCheckboxComponent } from './components/dropdown-checkbox/dropdown-checkbox.component';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { UseCasePlanSummaryComponent } from './components/use-case-plan-summary/use-case-plan-summary.component';
import { EditorUserComponent } from './components/editor-user/editor-user.component';
import { ExternalServiceAuthComponent } from './components/external-service-auth/external-service-auth.component';
import { FallbackImageDirective } from './directives/fallback-image.directive';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FilterByPropertyLabelPipe } from '@pipes/filter-by-property-label.pipe';
import { ComboboxOptionsPipe } from '@pipes/combobox-options.pipe';
import { TagsOptionsPipe } from '@pipes/tags-options.pipe';
import { FormTabsComponent } from './components/form-tabs/form-tabs.component';
import { FormToggleButtonsComponent } from './components/form-toggle-buttons/form-toggle-buttons.component';
import { GeneralFilterPipe } from '@pipes/general-filter.pipe';
import { GeneralFormEditorComponent } from './components/general-form-editor/general-form-editor.component';
import { GroupByPipe } from '@pipes/group-by.pipe';
import { HighlightPipe } from '@pipes/highlight.pipe';
import { HumanizeNumberPipe } from '@pipes/humanize-number.pipe';
import { ImageCropperComponent } from './components/image-cropper/image-cropper.component';
import { ImageUploadComponent } from '../core/components/image-upload/image-upload.component';
import { LanguagePillsComponent } from './components/language-pills/language-pills.component';
import { LoadingComponent } from '../core/components/loading/loading.component';
import { LoadingCardComponent } from '../core/components/loading-card/loading-card.component';
import { InViewDirective } from '../core/directives/in-view.directive';
// * Services
import { LoggerService } from '@services/logger.service';
// * Directives
import { SortableListDirective } from '../core/directives/sortable-list.directive';
import { MoveCopyOtherDashboardsComponent } from './components/move-copy-widget-dialog/move-copy-other-dashboards/move-copy-other-dashboards.component';
import { MoveCopyOtherFeedsComponent } from './components/move-copy-widget-dialog/move-copy-other-feeds/move-copy-other-feeds.component';
import { MoveCopySameDashboardComponent } from './components/move-copy-widget-dialog/move-copy-same-dashboard/move-copy-same-dashboard.component';
import { MoveCopySameFeedComponent } from './components/move-copy-widget-dialog/move-copy-same-feed/move-copy-same-feed.component';
import { MoveCopyWidgetDialogComponent } from './components/move-copy-widget-dialog/move-copy-widget-dialog.component';
// * Angular core & common modules
import { NgModule } from '@angular/core';
import { ImageCropperComponent as NgxImageCropperComponent } from 'ngx-image-cropper';
import { PageLayoutComponent } from './components/layouts/page-layout/page-layout.component';
import { PasswordStrengthComponent } from './components/password-strength/password-strength.component';
import { PrincipalCardComponent } from './components/cards/principal-card/principal-card.component';
import { ProgressBarComponent } from './components/blocks/progress-bar/progress-bar.component';
import { ProgressStepsComponent } from './components/progress-steps/progress-steps.component';
import { ReleaseNotesComponent } from './components/blocks/release-notes/release-notes.component';
import { RouterModule } from '@angular/router';
import { SafeHtmlPipe } from '@pipes/safe-html.pipe';
import { SafePipe } from '@pipes/safe.pipe';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { SelectedDataSourceComponent } from './components/selected-data-source/selected-data-source.component';
import { SortPipe } from '@pipes/sort.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { UndeterminedProgressBarComponent } from './components/undetermined-progress-bar/undetermined-progress-bar.component';
import { ExportSchedulesGridComponent } from './components/export-schedules-grid/export-schedules-grid.component';
import { UserActivityLogsComponent } from './components/user-activity-logs/user-activity-logs.component';
import { UserLinkedAccountsComponent } from '../admin/components/user-linked-accounts/user-linked-accounts.component';
import { UserOutputLogsComponent } from '../admin/components/user-output-logs/user-output-logs.component';
import { UserSearchLogsComponent } from '../admin/components/user-search-logs/user-search-logs.component';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';

import { SuiButtonComponent } from '@sui/button';
import { SuiButtonGroupComponent } from '@sui/button-group';
import { SUI_DIALOG_SIMPLE_SLOTS, SuiDialogSimpleComponent } from '@sui/dialog';
import { SuiInputComponent } from '@sui/input';
import { SuiFileInputComponent } from '@sui/file-input';
import { SuiTextareaComponent } from '@sui/textarea';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { SuiSwitchComponent } from '@sui/switch';
import { SuiToggleComponent } from '@sui/toggle';
import { SuiComboboxComponent } from '@sui/combobox';
import { SuiToolbarComponent } from '@sui/toolbar';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { SuiTagsComponent } from '@sui/tags';
import { SuiRangeSliderComponent, SuiSliderComponent } from '@sui/slider';
import { SuiRadioComponent, SuiRadioGroupDirective } from '@sui/radio';
import {
  SuiFormFieldComponent,
  SuiLabelDirective,
  SuiErrorDirective,
  SuiHintDirective,
} from '@sui/form-field';
import { SuiTooltipDirective } from '@sui/tooltip';
import {
  SuiMenuTriggerDirective,
  SuiSubmenuTriggerDirective,
  SuiMenuComponent,
  SuiMenuItemComponent,
  SuiMenuSeparatorComponent,
  SuiMenuHeaderComponent,
} from '@sui/menu';
import { SuiPopoverTriggerDirective, SuiPopoverDirective } from '@sui/popover';
import { SuiPaginationComponent } from '@sui/pagination';
import {
  SuiAccordionComponent,
  SuiAccordionItemComponent,
} from '@sui/accordion';
import {
  SuiDatePickerComponent,
  SuiDateRangePickerComponent,
  SuiDateTimePickerComponent,
} from '@sui/date-picker';

@NgModule({
  imports: [
    // * Compositional grid stack (no SharedModule dependency; re-exported below)
    DataGridModule,
    LoadingComponent,

    // * Angular modules
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    AgGridModule,
    DragDropModule,

    // * Third-party modules
    TranslateModule.forChild(),
    AngularSvgIconModule,
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    FontAwesomeModule,
    NgxImageCropperComponent,
    NgpPopover,
    NgpPopoverTrigger,
    SortableListDirective,

    SuiDialogSimpleComponent,
    ...SUI_DIALOG_SIMPLE_SLOTS,
    SuiButtonComponent,
    SuiButtonGroupComponent,
    SuiInputComponent,
    SuiFileInputComponent,
    SuiTextareaComponent,
    SuiCheckboxComponent,
    SuiSwitchComponent,
    SuiToggleComponent,
    SuiComboboxComponent,
    SuiToolbarComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiTagsComponent,
    SuiSliderComponent,
    SuiRangeSliderComponent,
    SuiRadioComponent,
    SuiRadioGroupDirective,
    SuiFormFieldComponent,
    SuiLabelDirective,
    SuiErrorDirective,
    SuiHintDirective,
    SuiTooltipDirective,
    SuiMenuTriggerDirective,
    SuiSubmenuTriggerDirective,
    SuiMenuComponent,
    SuiMenuItemComponent,
    SuiMenuSeparatorComponent,
    SuiMenuHeaderComponent,
    SuiPopoverTriggerDirective,
    SuiPopoverDirective,
    SuiPaginationComponent,
    SuiAccordionComponent,
    SuiAccordionItemComponent,
    SuiDatePickerComponent,
    SuiDateRangePickerComponent,
    SuiDateTimePickerComponent,

    AlgorithmScheduleComponent,
  ],
  declarations: [
    // * Components
    JsonEditorComponent,
    ColorPaletteComponent,
    CompanySwitcherComponent,
    CounterBadgeComponent,
    DatasourceSelectorComponent,
    DateRangePickerComponent,
    DateRangePickerDashboardPageComponent,
    PeriodHistoryComponent,
    DynamicFormComponent,
    UseCasePlanSummaryComponent,
    EditorUserComponent,
    ExternalServiceAuthComponent,
    FileUploadComponent,
    FormDateRangePickerComponent,
    FormTabsComponent,
    FormToggleButtonsComponent,
    GeneralFormEditorComponent,
    ImageUploadComponent,
    ImageCropperComponent,
    LanguagePillsComponent,
    LoadingCardComponent,
    InViewDirective,
    PageLayoutComponent,
    PasswordStrengthComponent,
    PrincipalCardComponent,
    ProgressBarComponent,
    ProgressStepsComponent,
    ReleaseNotesComponent,
    SearchInputComponent,
    SelectedDataSourceComponent,
    DataObjectSelectComponent,
    ExportSchedulesGridComponent,
    UserActivityLogsComponent,
    MoveCopyWidgetDialogComponent,
    AutoStoryToolDialogComponent,
    LinkRelatedArticlesDialogComponent,
    I18nKeyRejectedDialogComponent,
    MoveCopySameDashboardComponent,
    MoveCopySameFeedComponent,
    MoveCopyOtherDashboardsComponent,
    MoveCopyOtherFeedsComponent,
    DialogTabNavComponent,
    DashboardAutoRefreshCounterComponent,
    UserSearchLogsComponent,
    UserOutputLogsComponent,
    UserLinkedAccountsComponent,
    ColorPickerComponent,

    // * Directives
    FallbackImageDirective,

    // * Pipes
    CountFilteredPipe,
    FilterByPropertyLabelPipe,
    ComboboxOptionsPipe,
    TagsOptionsPipe,
    GeneralFilterPipe,
    HighlightPipe,
    SafeHtmlPipe,
    SafePipe,
    SortPipe,
    CustomFilterOptionsPipe,
    ChatMessageInputComponent,
    CounterBadgeComponent,
    UndeterminedProgressBarComponent,
    DropdownCheckboxComponent,
    CompanyBadgeComponent,
    AdvancedQuickSearchComponent,
    HumanizeNumberPipe,
    DataobjectListSimpleComponent,
    BannerContainerComponent,
    ContextualNotFoundComponent,
    CountGroupMatchesPipe,
    GroupByPipe,
  ],
  exports: [
    DataGridModule,
    NgpTabset,
    NgpTabList,
    NgpTabButton,
    // * Angular modules
    RouterModule,
    FormsModule,
    ReactiveFormsModule,

    // * Third-party modules
    TranslateModule,
    AngularSvgIconModule,
    FontAwesomeModule,
    NgpPopover,
    NgpPopoverTrigger,

    // * Components
    JsonEditorComponent,
    BannerContainerComponent,
    CompanySwitcherComponent,
    ColorPaletteComponent,
    CompanyBadgeComponent,
    CounterBadgeComponent,
    DatasourceSelectorComponent,
    DateRangePickerComponent,
    DateRangePickerDashboardPageComponent,
    PeriodHistoryComponent,
    DataObjectSelectComponent,
    DropdownCheckboxComponent,
    DynamicFormComponent,
    UseCasePlanSummaryComponent,
    EditorUserComponent,
    ExternalServiceAuthComponent,
    FileUploadComponent,
    FormDateRangePickerComponent,
    FormTabsComponent,
    FormToggleButtonsComponent,
    GeneralFormEditorComponent,
    ImageUploadComponent,
    ImageCropperComponent,
    LanguagePillsComponent,
    LoadingComponent,
    LoadingCardComponent,
    InViewDirective,
    PageLayoutComponent,
    PasswordStrengthComponent,
    PrincipalCardComponent,
    ProgressBarComponent,
    ProgressStepsComponent,
    ReleaseNotesComponent,
    SearchInputComponent,
    SelectedDataSourceComponent,
    UndeterminedProgressBarComponent,
    AdvancedQuickSearchComponent,
    ExportSchedulesGridComponent,
    UserActivityLogsComponent,
    DataobjectListSimpleComponent,
    MoveCopyWidgetDialogComponent,
    AutoStoryToolDialogComponent,
    LinkRelatedArticlesDialogComponent,
    I18nKeyRejectedDialogComponent,
    ContextualNotFoundComponent,
    DialogTabNavComponent,
    DashboardAutoRefreshCounterComponent,
    UserSearchLogsComponent,
    UserOutputLogsComponent,
    UserLinkedAccountsComponent,
    ColorPickerComponent,
    SuiButtonComponent,
    SuiButtonGroupComponent,
    SuiInputComponent,
    SuiFileInputComponent,
    SuiCheckboxComponent,
    SuiComboboxComponent,
    SuiToolbarComponent,
    SuiSelectComponent,
    SuiSelectOptionComponent,
    SuiTextareaComponent,
    SuiTagsComponent,
    SuiSliderComponent,
    SuiPaginationComponent,
    SuiAccordionComponent,
    SuiAccordionItemComponent,
    SuiDatePickerComponent,
    SuiDateRangePickerComponent,
    SuiDateTimePickerComponent,
    SuiRadioComponent,
    SuiRadioGroupDirective,
    SuiSwitchComponent,
    SuiToggleComponent,
    SuiFormFieldComponent,
    SuiLabelDirective,
    SuiErrorDirective,
    SuiHintDirective,
    SuiTooltipDirective,
    SuiMenuTriggerDirective,
    SuiSubmenuTriggerDirective,
    SuiMenuComponent,
    SuiMenuItemComponent,
    SuiMenuSeparatorComponent,
    SuiMenuHeaderComponent,
    SuiPopoverTriggerDirective,
    SuiPopoverDirective,
    // * Directives
    FallbackImageDirective,

    // * Pipes
    CountFilteredPipe,
    CountGroupMatchesPipe,
    FilterByPropertyLabelPipe,
    ComboboxOptionsPipe,
    TagsOptionsPipe,
    GeneralFilterPipe,
    HighlightPipe,
    SafeHtmlPipe,
    SafePipe,
    SortPipe,
    HumanizeNumberPipe,
    GroupByPipe,
  ],
  providers: [LoggerService, ColorPaletteComponent],
})
export class SharedModule {
  constructor(library: FaIconLibrary, faConfig: FaConfig) {
    // A null/undefined [icon] renders this instead of throwing mid-render (WAP-1738, WAP-1747).
    faConfig.fallbackIcon = faTriangleExclamation;

    // Add icon packs for convenient access
    library.addIconPacks(fas, far, fab);

    // Add a custom icon to the library
    const mattermostIcon: IconDefinition = {
      prefix: 'fas' as IconPrefix,
      iconName: 'turn-up' as IconName,
      icon: [
        500, // width
        500, // height
        [], // ligatures
        '', // unicode
        'M 250.05,34.00 C 251.95,34.04 253.85,34.11 255.65,34.20 255.65,34.20 225.86,69.71 225.86,69.71 225.79,69.72 225.71,69.74 225.63,69.75 149.26,84.10 98.22,146.50 98.22,222.97 98.22,264.53 121.29,313.47 157.97,342.07 186.58,364.39 222.26,378.97 259.18,378.97 352.58,378.97 419.33,310.36 419.33,222.97 419.33,188.06 403.34,150.20 377.57,122.21 377.57,122.21 375.94,74.82 375.94,74.82 430.39,113.97 465.89,177.84 466.00,249.99 466.00,250.00 466.00,250.00 466.00,250.00 466.00,369.29 369.30,466.00 250.00,466.00 130.71,466.00 34.00,369.29 34.00,250.00 34.00,130.71 130.71,34.00 250.00,34.00 250.00,34.00 250.05,34.00 250.05,34.00 Z M 314.15,54.29 C 314.81,54.25 315.47,54.32 316.11,54.54 319.12,55.54 319.96,58.11 320.04,60.99 320.04,60.99 323.88,207.87 323.88,207.87 324.64,236.53 306.72,276.31 263.49,276.43 232.52,276.51 199.81,255.60 199.81,216.30 199.82,201.57 205.42,185.04 219.06,168.19 219.06,168.19 309.09,57.01 309.09,57.01 310.24,55.59 312.17,54.43 314.15,54.29 314.15,54.29 314.15,54.29 314.15,54.29 Z',
      ],
    };

    library.addIcons(mattermostIcon);

    // +––– BEGIN custom 9-dots nav-grid icon –––+
    const navGridIcon: IconDefinition = {
      prefix: 'fas' as IconPrefix,
      iconName: 'grid' as IconName,
      icon: [
        24, // viewBox width
        24, // viewBox height
        [], // ligatures
        '', // unicode
        // path data from your provided SVG
        'M18.5 1A1.5 1.5 0 0 0 17 2.5v3A1.5 1.5 0 0 0 18.5 7h3A1.5 1.5 0 0 0 23 5.5v-3A1.5 1.5 0 0 0 21.5 1h-3zm0 8a1.5 1.5 0 0 0-1.5 1.5v3a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-3A1.5 1.5 0 0 0 21.5 9h-3zm-16 8A1.5 1.5 0 0 0 1 18.5v3A1.5 1.5 0 0 0 2.5 23h3A1.5 1.5 0 0 0 7 21.5v-3A1.5 1.5 0 0 0 5.5 17h-3zm8 0A1.5 1.5 0 0 0 9 18.5v3a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-3a1.5 1.5 0 0 0-1.5-1.5h-3zm8 0a1.5 1.5 0 0 0-1.5 1.5v3a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-3a1.5 1.5 0 0 0-1.5-1.5h-3zm-16-8A1.5 1.5 0 0 0 1 10.5v3A1.5 1.5 0 0 0 2.5 15h3A1.5 1.5 0 0 0 7 13.5v-3A1.5 1.5 0 0 0 5.5 9h-3zm0-8A1.5 1.5 0 0 0 1 2.5v3A1.5 1.5 0 0 0 2.5 7h3A1.5 1.5 0 0 0 7 5.5v-3A1.5 1.5 0 0 0 5.5 1h-3zm8 0A1.5 1.5 0 0 0 9 2.5v3A1.5 1.5 0 0 0 10.5 7h3A1.5 1.5 0 0 0 15 5.5v-3A1.5 1.5 0 0 0 13.5 1h-3zm0 8A1.5 1.5 0 0 0 9 10.5v3a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5v-3A1.5 1.5 0 0 0 13.5 9h-3z',
      ],
    };

    library.addIcons(navGridIcon);
    // +––– END custom icon –––+
  }
}
